import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";

interface VisibilityButtonProps {
  visibility: boolean;
  setVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  active?: boolean;
}

const VisibilityButton: React.FC<VisibilityButtonProps> = ({
  visibility,
  setVisibility,
  active = true,
}) => {
  // States definition
  const [blockToggle, setBlockToggle] = useState(false);

  // Parameters
  const visibilityTransitionMs = 0.2;

  /**
   * Function executed when clicking on toggle password visibility.
   * This mechanism is necessary to avoid UI problems when clicking
   * too fast this icon.
   */
  const toggleVisibility = () => {
    // Check first if the button is active to block the toggle button
    if (active) setBlockToggle(true);
  };

  /**
   * Function executed every time the toggle visibility has changed
   */
  useEffect(() => {
    if (blockToggle) {
      // Toggle the visibility
      setVisibility(!visibility);

      // Delay the visibility update to allow the exit animation to finish
      setTimeout(
        () => {
          // And release toggle button
          setBlockToggle(false);
        },
        visibilityTransitionMs * 1000 + 20,
      ); // delay exceeds the exit animation duration to avoid overclicking errors
    }
  }, [blockToggle]);

  return (
    <div
      className={`absolute w-fit h-fit p-2 right-1.5 ${active ? "cursor-pointer hover:bg-neutral-100 ease-in-out duration-150" : "cursor-default"} z-20 rounded-full`}
      onClick={toggleVisibility}
    >
      <AnimatePresence mode="wait">
        {visibility ? (
          <motion.div
            key="visible"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: visibilityTransitionMs }}
          >
            <MdOutlineVisibility
              className={`w-5 h-5 ${active ? "fill-neutral-500" : "fill-neutral-300"}`}
            />
          </motion.div>
        ) : (
          <motion.div
            key="hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: visibilityTransitionMs }}
          >
            <MdOutlineVisibilityOff
              className={`w-5 h-5 ${active ? "fill-neutral-500" : "fill-neutral-300"}`}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisibilityButton;
